import React, { useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  convertToNotificationDate,
  getItemsCreatedMoreThanTwoDaysAgo,
  getItemsCreatedToday,
  getItemsCreatedYesterday,
  getRelativeTime,
  getTimeOnly,
  trackNotificationViewAllClick,
} from 'helpers';
import styles from './styles/NotificationList.module.scss';
import { NOTIFICATIONS_ROUTE } from 'constants/routes';
import { IAppState, INotification } from 'types';
import { selectNotificationState } from 'selectors';
import { SeenNotifications, UpdateNotification } from 'actions';

import {
  getDotClassNameByType,
  getText,
  handleNotificationClick,
  removeDuplicateNotifications,
} from './utils';
import { INSURANCE_TYPES_ICONS_SVG_V2 } from 'constants/insurance';
import { Star } from 'react-feather';
import { TASK_TYPES } from 'constants/notification';
import classnames from 'classnames';

// Lazy load heavy components
const LoaderComponent = lazy(() =>
  import('semantic-ui-react').then((module) => ({ default: module.Loader })),
);

const POPUP_MAX_COUNT = 5;

const NotificationItem = React.memo(
  ({
    className,
    period,
    onClick,
    hideNotification,
    ...notification
  }: INotificationItem) => {
    const dispatch = useDispatch();
    const appState: IAppState = useSelector(R.identity);
    const { title, subtitle, description } = getText(notification, appState);
    const { id, clicked, startDate, createdAt, policyType, notificationType } =
      notification;
    const Icon = INSURANCE_TYPES_ICONS_SVG_V2[policyType] || R.T;

    const handleClick = useCallback(
      (e) => {
        if (!notification.clicked) {
          dispatch(
            UpdateNotification({ ...notification, clicked: true, seen: true }),
          );
        }
        handleNotificationClick(notification, dispatch, appState);
        if (onClick) onClick();
      },
      [notification, dispatch, appState, onClick],
    );

    return (
      <div
        key={id}
        onClick={handleClick}
        className={classnames(
          styles.item,
          className,
          clicked && styles.clicked,
        )}
      >
        <div className={styles.wrap}>
          <div className={styles.content}>
            {startDate ? (
              <Star className={styles.star} />
            ) : (
              <div
                className={classnames(
                  styles.dot,
                  getDotClassNameByType(notificationType),
                )}
              />
            )}
            <div className={styles.text}>
              <div className={styles.subTextOne}>
                <b className={styles.title}>{title}</b>
                <div className={styles.subtitle}>{subtitle}</div>
              </div>
              <div className={styles.subTextTwo}>
                <Icon className={styles.icon} />
                <div className={styles.description}>
                  {description ? description : ''}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.time}>
            <span className={styles.relative}>
              {period === 'Today' || period === 'Yesterday'
                ? getTimeOnly(startDate || createdAt)
                : getRelativeTime(startDate || createdAt)}
            </span>
            <span className={styles.longDate}>
              {period === 'Today' || period === 'Yesterday'
                ? getTimeOnly(startDate || createdAt)
                : convertToNotificationDate(startDate || createdAt)}
            </span>
          </div>
        </div>
      </div>
    );
  },
);

const NotificationItemList = React.memo(
  ({
    notificationItems,
    period,
    hideNotification,
    onClick,
    isMobile,
  }: INotificationItemList & { isMobile?: boolean }) => {
    return isMobile ? (
      period === 'Today' ? (
        <div>
          {notificationItems.length > 0 && (
            <NotificationItem
              period={period}
              hideNotification={hideNotification}
              onClick={onClick}
              {...notificationItems[0]}
            />
          )}
        </div>
      ) : null
    ) : (
      <div>
        {notificationItems.length > 0 && (
          <div className={styles.header}>{period}</div>
        )}
        {notificationItems.map((notification) => (
          <NotificationItem
            key={notification.id}
            period={period}
            hideNotification={hideNotification}
            onClick={onClick}
            {...notification}
          />
        ))}
      </div>
    );
  },
);

const NotificationsGroupedDate = React.memo(
  ({
    notifications,
    onClick,
    hideNotification,
    showAll,
    isMobile,
  }: INotificationsGroupedDate & { isMobile?: boolean }) => {
    const todaysNotifications = getItemsCreatedToday(notifications);
    const yesterdaysNotifications = getItemsCreatedYesterday(notifications);
    const previousNotifications =
      getItemsCreatedMoreThanTwoDaysAgo(notifications);

    return (
      <>
        <NotificationItemList
          notificationItems={todaysNotifications}
          period="Today"
          onClick={onClick}
          hideNotification={hideNotification}
          showAll={showAll}
          isMobile={isMobile}
        />
        <NotificationItemList
          notificationItems={yesterdaysNotifications}
          period="Yesterday"
          onClick={onClick}
          hideNotification={hideNotification}
          showAll={showAll}
          isMobile={isMobile}
        />
        <NotificationItemList
          notificationItems={previousNotifications}
          period="Previous"
          onClick={onClick}
          hideNotification={hideNotification}
          showAll={showAll}
          isMobile={isMobile}
        />
      </>
    );
  },
);

const NotificationList = ({
  showAll,
  handleClose,
  isMobile,
}: IProps & { isMobile?: boolean }) => {
  const { loading, notifications } = useSelector(selectNotificationState);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      const toSee = notifications.filter((n) => !n.seen);
      if (toSee.length > 0) {
        dispatch(SeenNotifications(toSee));
      }
    };
  }, [notifications, dispatch]);

  const hideNotification = useCallback(
    (notification: INotification) => {
      const hidden = R.assoc('hide', true, notification);
      dispatch(UpdateNotification(hidden));
    },
    [dispatch],
  );

  const notificationsToShow = useMemo(() => {
    return showAll
      ? notifications
      : removeDuplicateNotifications(R.take(POPUP_MAX_COUNT, notifications));
  }, [notifications, showAll]);

  return (
    <div className={classnames(!showAll && styles.compact)}>
      <div className={styles.items}>
        {loading ? (
          <Suspense
            fallback={
              <div className={styles.spinnerContainer}>
                <LoaderComponent className={styles.spinner} active={true} />
              </div>
            }
          >
            <LoaderComponent className={styles.spinner} active={true} />
          </Suspense>
        ) : (
          notificationsToShow.length === 0 && (
            <div className={styles.emptyItem}>No notifications</div>
          )
        )}
      </div>
      <NotificationsGroupedDate
        notifications={notificationsToShow}
        onClick={handleClose}
        hideNotification={hideNotification}
        showAll={showAll}
        isMobile={isMobile}
      />
      {!showAll && notifications.length > POPUP_MAX_COUNT && !isMobile && (
        <Link
          className={styles.seeAll}
          to={NOTIFICATIONS_ROUTE}
          onClick={() => trackNotificationViewAllClick()}
        >
          <span>See all notifications</span>
        </Link>
      )}
    </div>
  );
};

export { NotificationItem };
export default NotificationList;
